<script lang="ts" generics="MODEL_VALUE">
    import {getInjector} from 'src/core';
    import type {CustomValueEditorLocalizations, ValueEditorTypeOrAlias} from '../types';
    import {ValueEditorSize} from '../types';
    import type {ValueEditorOptions, ValueEditorValidations} from './types';
    import type {ModelInputChangeEvent, ValueEditorsEventMap} from '../events';
    import type {FormController, FormControlOptions} from '../internal/forms/types';
    import type {ValueEditorLocalizations} from 'shared/value-editors/localizations/types';
    import type {Readable} from 'svelte/store';
    import type {Subscription} from 'rxjs';
    import type {FormGroupBuilder} from '../internal/forms/form-group';
    import type {ForceSettingsOptionsMerger} from '../kp-value-editor-force-settings/force-settings-options-merger';
    import {createEventDispatcher, getContext, onDestroy, onMount} from 'svelte';
    import {ValueEditorTypeResolutionService} from './value-editor-type-resolution.service';
    import {ValueEditorConfigurationManagerProvider} from '../configuration/value-editor-configuration-manager.provider';
    import {ValueEditorLocalizationManagerProvider} from '../localizations/value-editor-localization-manager.provider';
    import {exists, uuid as generateUuid} from 'shared/utils/custom-utils';
    import {ErrorMessagesLocalizationService} from '../internal/errors/error-messages-localization.service';
    import {KpValueEditorConfigurationService} from './kp-value-editor-configuration.service';
    import {isMetaEditor, setEditorsLocalizationContext, setErrorsLocalizationContext} from './utils';
    import {merge} from 'lodash-es';
    import {ASYNC_VALIDATION_DEFAULT_OPTIONS, asyncValidator} from '../internal/shared/async-validations';
    import {FORCE_SETTINGS, FORM_BUILDER, UNIVERSAL_FORM_MODEL} from '../context-keys';
    import {defaultUpdateTriggers} from '../internal/forms/form-control';

    export let type: ValueEditorTypeOrAlias;

    export let model: MODEL_VALUE;
    export let formControlOptions: FormControlOptions | null = null;

    export let editorId: string = null;
    export let editorName: string = editorId ?? `${type}_${generateUuid()}`;
    export let placeholder = '';

    export let isDisabled = false;
    export let isVisible = true;
    export let isFocused = false;
    export let forceShowErrors = false;
    export let size: ValueEditorSize = ValueEditorSize.MD;

    export let options: ValueEditorOptions = {};
    export let validations: ValueEditorValidations = {};
    export let localizations: CustomValueEditorLocalizations<ValueEditorLocalizations> = null;

    const dispatch = createEventDispatcher<ValueEditorsEventMap<MODEL_VALUE>>();

    const configuration = getInjector().getByClass(KpValueEditorConfigurationService);
    const errorMessagesLocalizationService = getInjector().getByClass(ErrorMessagesLocalizationService);
    const editorResolutionService = getInjector().getByClass(ValueEditorTypeResolutionService);
    const configurationManagerProvider = getInjector().getByClass(ValueEditorConfigurationManagerProvider);
    const localizationManagerProvider = getInjector().getByClass(ValueEditorLocalizationManagerProvider);

    const realType = editorResolutionService.resolveRealType(type);
    const editor = editorResolutionService.getEditorComponentByTypeOrAlias(type);
    const universalFormModel$: Readable<Record<string, unknown>> = getContext(UNIVERSAL_FORM_MODEL);
    const formBuilder: FormGroupBuilder<Record<string, unknown>> = getContext(FORM_BUILDER);
    const forceSettings: ForceSettingsOptionsMerger = getContext(FORCE_SETTINGS);
    let editorController: any; // component binding
    let modelChangesSubscription: Subscription;

    setEditorsLocalizationContext(localizations, localizationManagerProvider.getManagerForType(realType));
    setErrorsLocalizationContext(localizations, errorMessagesLocalizationService);

    function processOptions(newOptions: ValueEditorOptions) {
        const optionsFromConfiguration = configurationManagerProvider.getManagerForType(realType).forAlias(type).getConfiguration();

        let updatedOptions = merge({}, optionsFromConfiguration, newOptions);

        if (forceSettings) {
            updatedOptions = forceSettings.mergeOptionsForType(type, updatedOptions)
        }

        return updatedOptions;
    }

    $: options = processOptions(options);

    if (validations.async && !formControlOptions) {
        // add debounce if async validation is present
        formControlOptions = {updateTriggers: [defaultUpdateTriggers.BLUR, {eventName: 'input', debounceValue: 500}]};
    }

    function dispatchModelChangeEvent(event: ModelInputChangeEvent<MODEL_VALUE>) {
        // Event fired when model value is changed via some input element. Payload is new model value.
        dispatch('model-change', event.detail);
        // literal event name instead of constant is used, because otherwise STORYBOOK does not recognize the event
    }

    onMount(() => {
        modelChangesSubscription = getFormController().getModelInputChangeEvent$().subscribe((event) => {
            dispatchModelChangeEvent(event);
        });

        // add async validations
        if (validations.async) {
            const validationFunction = configuration.getValidationsFunction();
            let nullIfEmptyModifier: <T>(value: T) => T | null;
            if (options?.emptyAsNull) {
                nullIfEmptyModifier = (value) => !value ? null : value;
            }
            const asyncOptions = typeof validations.async === 'boolean' ? ASYNC_VALIDATION_DEFAULT_OPTIONS : {...ASYNC_VALIDATION_DEFAULT_OPTIONS, ...validations.async};
            getFormController().addAsyncValidator(asyncValidator(validationFunction, errorMessagesLocalizationService, editorName, universalFormModel$, asyncOptions, nullIfEmptyModifier))
            getFormController().validate();
            if (exists(model)) {
                getFormController().setTouched();
            }
        }

        if (formBuilder) {
            formBuilder.registerFormControl(getFormController()); // register to the nearest parent meta editor
        }
    });

    onDestroy(() => {
        modelChangesSubscription?.unsubscribe();
    });

    export function getFormController(): FormController<MODEL_VALUE> {
        return editorController.getFormController();
    }
</script>

<kp-value-editor class:hidden={!isVisible} data-type={realType} data-type-or-alias={type}>
    <svelte:component this={editor}
                      bind:this={editorController}

                      bind:model
                      {formControlOptions}

                      {editorId}
                      {editorName}
                      {placeholder}


                      {...isMetaEditor(realType) /* conditional 'isVisible' property */ ? {isVisible} : {}}
                      {isDisabled}
                      {isFocused}
                      {forceShowErrors}
                      {size}

                      options={options ?? {}}
                      validations={validations ?? {}}

                      on:form-submit-request
                      on:form-structure-change/>
</kp-value-editor>

<style lang="less">
    @import "bootstrap-less/bootstrap/variables";
    @import "bootstrap-less/bootstrap/mixins/forms";
    @import "bootstrap-less/bootstrap/mixins/vendor-prefixes";

    :global {
        .has-error {
            kp-value-editor {
                .input-group .btn-default, .form-control-button {
                    border-color: @state-danger-text;
                    transition: border-color 0.15s ease-in-out;
                }

                // reset error styles if editor is nested in meta-editor which has error
                .has-not-error {
                    .input-group .btn-default, .form-control-button {
                        border-color: @input-border;
                        transition: border-color 0.15s ease-in-out;
                    }

                    .form-control-validation(@input-color, @input-border, @input-bg);
                }
            }
        }
    }
</style>